// PenStatusBadge — persistent BLE-pen indicator mounted in the app shell.
// Reads from <LiveStreamProvider> and renders pH / EC / temp at glanceable
// size so the grower can read values across the room.
//
// Issue 5: a single tone used to conflate THREE different failures — in
// particular "disconnected" meant BOTH "browser↔server WebSocket is down"
// AND "WebSocket up but no iOS publisher reporting." Those need different
// fixes, so the badge now shows a single summary dot (reflecting the WORST
// of three layers) and the full breakdown lives behind a popover:
//
//   1. Server  — browser ↔ server WebSocket
//   2. iOS app — the publisher forwarding pen data (inferred from frames)
//   3. Pen     — the BLE link to the physical pen
//
// The layer-derivation logic + popover body live in ./pen-status-detail.
//
// Per-metric interaction (unchanged):
//   - single click on a metric → cycle through its available units
//   - dedicated copy button    → copy a concise summary of all metrics
//                                ("pH: 5.82, EC: 1.34, Temp: 21.5")
//
// pH has no alternate unit and is unclickable. The copy button surfaces a
// brief "copied" flash for feedback without a global toast system.

import { useCallback, useEffect, useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  derivePenLayers,
  headlineStatus,
  levelDotClass,
  levelTextClass,
  PenStatusDetail,
  type LayerLevel,
} from "@/components/pen-status-detail";
import { useLiveStream } from "@/lib/livestream/use-livestream";
import { usePreferences } from "@/lib/preferences";
import {
  ecUnitLabel,
  formatEc,
  formatTemp,
  nextEcUnit,
  nextTempUnit,
  tempUnitLabel,
} from "@/lib/units";
import { cn } from "@/lib/utils";

interface Props {
  /** When true (default), shows pH/EC/temp side-by-side at glanceable size.
   *  When false, collapses to the legacy single-metric chip. */
  expanded?: boolean;
}

export function PenStatusBadge({ expanded = true }: Props) {
  const { state, lastStatus, lastSample, publisherOnline, sendCommand } =
    useLiveStream();
  const { temperatureUnit, ecUnit, setTemperatureUnit, setEcUnit } =
    usePreferences();
  const [now, setNow] = useState(() => Date.now());

  // Nudge the publisher (iOS) to reconnect its BLE pen and re-assert our
  // stream demand. Surfaced from the status popover so recovering a dropped
  // pen no longer means navigating away and reloading the app.
  const reconnect = useCallback(() => {
    sendCommand({ action: "reconnect_pen" });
    sendCommand({
      action: "set_pen_stream",
      set_pen_stream: { enabled: true, rate_hz: 1.0 },
    });
  }, [sendCommand]);

  // Tick once per second while a status is known, so the summary dot degrades
  // as freshness windows elapse even if no new frame arrives.
  useEffect(() => {
    if (!lastStatus && !lastSample) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lastStatus, lastSample]);

  const layers = derivePenLayers({
    state,
    lastStatus,
    lastSample,
    publisherOnline,
    now,
  });
  const head = headlineStatus(layers);
  const live = layers.pen.level === "up";

  if (!expanded) {
    const ph = lastSample?.sample.ph_filtered ?? lastSample?.sample.ph;
    const label =
      live && typeof ph === "number" ? `pH ${ph.toFixed(2)}` : head.label;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs transition-colors hover:bg-accent/40",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              levelTextClass(head.level),
            )}
            aria-label="Connection status"
            title="Connection status"
          >
            <SummaryDot level={head.level} pulse={head.pulse} />
            {label}
            <Caret />
          </button>
        </PopoverTrigger>
        <StatusPopoverContent
          state={state}
          lastStatus={lastStatus}
          lastSample={lastSample}
          publisherOnline={publisherOnline}
          onReconnect={reconnect}
        />
      </Popover>
    );
  }

  const ph = lastSample?.sample.ph_filtered ?? lastSample?.sample.ph;
  const ec = lastSample?.sample.ec_ms_per_cm;
  const temp = lastSample?.sample.temperature_c;

  return (
    <div className="inline-flex items-center gap-3 rounded-lg border bg-background px-3 py-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "-mx-1 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-accent/40",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              levelTextClass(head.level),
            )}
            aria-label="Connection status"
            title="Connection status"
          >
            <SummaryDot level={head.level} pulse={head.pulse} />
            <span className="text-xs font-medium whitespace-nowrap">
              {head.label}
            </span>
            <Caret />
          </button>
        </PopoverTrigger>
        <StatusPopoverContent
          state={state}
          lastStatus={lastStatus}
          lastSample={lastSample}
          publisherOnline={publisherOnline}
          onReconnect={reconnect}
        />
      </Popover>
      <Divider />
      <Metric label="pH" display={fmt(ph, 2)} live={live} />
      <Divider />
      <Metric
        label={ecUnitLabel(ecUnit)}
        display={formatEc(ec, ecUnit)}
        live={live}
        onToggleUnit={() => setEcUnit(nextEcUnit(ecUnit))}
        toggleHint={`Switch to ${nextEcUnit(ecUnit)}`}
      />
      <Divider />
      <Metric
        label={tempUnitLabel(temperatureUnit)}
        display={formatTemp(temp, temperatureUnit, 1)}
        live={live}
        onToggleUnit={() => setTemperatureUnit(nextTempUnit(temperatureUnit))}
        toggleHint={`Switch to ${tempUnitLabel(nextTempUnit(temperatureUnit))}`}
      />
      <Divider />
      <CopyAllButton
        summary={summarize({ ph, ec, temp, temperatureUnit, ecUnit })}
        disabled={!live}
      />
    </div>
  );
}

/** Caret — a small affordance signalling the status chip opens a popover. */
function Caret() {
  return (
    <span className="text-[10px] leading-none opacity-60" aria-hidden>
      ▾
    </span>
  );
}

/** SummaryDot — the badge's single dot, colored by the worst layer; pulses
 *  while the pen is live. */
function SummaryDot({ level, pulse }: { level: LayerLevel; pulse: boolean }) {
  return (
    <span
      className={cn(
        "inline-block size-2 rounded-full",
        levelDotClass(level),
        pulse ? "animate-pulse" : "",
      )}
      aria-hidden
    />
  );
}

/** StatusPopoverContent wraps the three-layer breakdown in a styled,
 *  accessible Radix popover panel. */
function StatusPopoverContent({
  state,
  lastStatus,
  lastSample,
  publisherOnline,
  onReconnect,
}: {
  state: ReturnType<typeof useLiveStream>["state"];
  lastStatus: ReturnType<typeof useLiveStream>["lastStatus"];
  lastSample: ReturnType<typeof useLiveStream>["lastSample"];
  publisherOnline: ReturnType<typeof useLiveStream>["publisherOnline"];
  onReconnect: () => void;
}) {
  return (
    <PopoverContent align="start" aria-label="Pen connection status">
      <PenStatusDetail
        state={state}
        lastStatus={lastStatus}
        lastSample={lastSample}
        publisherOnline={publisherOnline}
        onReconnect={onReconnect}
      />
    </PopoverContent>
  );
}

interface SummaryArgs {
  ph: number | undefined;
  ec: number | undefined;
  temp: number | undefined;
  temperatureUnit: ReturnType<typeof usePreferences>["temperatureUnit"];
  ecUnit: ReturnType<typeof usePreferences>["ecUnit"];
}

/** summarize builds the concise copy-all string, e.g.
 *    "pH: 5.82, EC: 1.34 mS/cm, Temp: 21.5 C"
 *    "pH: 5.82, EC: 938 ppm, Temp: 70.7 F"
 *  Units are included on EC + Temp because both can flip; pH has no alt unit. */
function summarize(a: SummaryArgs): string {
  const ecLabel = a.ecUnit === "ppm700" ? "ppm" : "mS/cm";
  const parts: string[] = [];
  parts.push(`pH: ${fmt(a.ph, 2)}`);
  parts.push(`EC: ${formatEc(a.ec, a.ecUnit)} ${ecLabel}`);
  parts.push(
    `Temp: ${formatTemp(a.temp, a.temperatureUnit, 1)} ${a.temperatureUnit}`,
  );
  return parts.join(", ");
}

/** CopyAllButton copies the summary string and briefly flashes "copied". */
function CopyAllButton({
  summary,
  disabled,
}: {
  summary: string;
  disabled: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const handle = useCallback(() => {
    if (disabled || !navigator.clipboard) return;
    void navigator.clipboard
      .writeText(summary)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1000);
      })
      .catch(() => {
        // insecure context or permissions denied; silently no-op
      });
  }, [summary, disabled]);

  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      title={disabled ? "Pen offline" : `Copy: ${summary}`}
      aria-label="Copy pen values"
      className={cn(
        "rounded p-1 text-muted-foreground transition-colors",
        "hover:bg-accent/40 hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-40",
        copied ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "",
      )}
    >
      {copied ? (
        // checkmark
        <svg className="size-4" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M3 8.5l3 3 7-7"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        // copy icon — two overlapping rounded squares
        <svg className="size-4" viewBox="0 0 16 16" fill="none" aria-hidden>
          <rect
            x="5"
            y="5"
            width="9"
            height="9"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.25"
          />
          <path
            d="M3.5 10.5V3.5A1.5 1.5 0 015 2h6"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function Divider() {
  return <span className="h-5 w-px bg-border" aria-hidden />;
}

interface MetricProps {
  label: string;
  display: string;
  /** When the pen layer is live, values render at full opacity. */
  live: boolean;
  /** When provided, clicking the metric cycles to the next display unit. */
  onToggleUnit?: () => void;
  /** Tooltip hint describing the next state (e.g. "Switch to °F"). */
  toggleHint?: string;
}

/**
 * Metric renders one pen-value cell. When onToggleUnit is provided, click
 * cycles to the next display unit. Cells without a toggle (pH) render as
 * a plain inline span — no false affordance.
 */
function Metric({ label, display, live, onToggleUnit, toggleHint }: MetricProps) {
  const body = (
    <>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "text-lg font-semibold tabular-nums leading-none",
          live ? "" : "opacity-50",
        )}
      >
        {display}
      </span>
    </>
  );

  if (!onToggleUnit) {
    return (
      <div className="-mx-1 flex select-none items-baseline gap-1.5 px-1">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      title={toggleHint ?? "Switch unit"}
      aria-label={`${label} ${display} — ${toggleHint ?? "switch unit"}`}
      onClick={onToggleUnit}
      className={cn(
        "-mx-1 flex items-baseline gap-1.5 rounded px-1 transition-colors",
        "hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "cursor-pointer select-none",
      )}
    >
      {body}
    </button>
  );
}

function fmt(n: number | undefined, digits: number): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}
